import {useEffect} from 'react'
import {useAuth} from '../stores/AuthStore'
import {api} from '../services/api'
import {registerSilentSessionRecovery} from '../services/api/http'

/**
 * How often the legacy admin session is slid forward.
 *
 * The server rotates the session on a 1-hour `systemrefresh` window (an `auth`
 * deployment flag — changing it is a security-posture decision in another repo,
 * deliberately out of scope). 15 minutes leaves three whole cycles of headroom
 * inside that window, which matters because a backgrounded tab has its timers
 * throttled hard: the refresh has to be able to fire late and still be early.
 *
 * It is a judgement call, not a measurement — it quadruples /v1/auth/refresh
 * traffic per active admin session versus the old hourly exchange. The endpoint
 * is one SQL read plus one CycleTokenID write and admin concurrency is low, so
 * watch `service:auth` after deploy rather than pre-optimising (AB#24880).
 */
const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000

export const SessionRefresher = (): null => {
  const {handleRefreshSession, useIdpAuthentication} = useAuth()

  // The 401 recovery in `services/api/http.ts` runs inside the axios response
  // interceptor, at module scope, where the Auth0 hooks the re-exchange needs
  // don't exist. Lend it this component's access to the auth store instead. It
  // lives here because this is already the session-maintenance component, and it
  // is mounted app-wide inside AuthProvider (AppProviders).
  useEffect(
    () =>
      registerSilentSessionRecovery(
        async () => (await handleRefreshSession({silent: true})) === 'success',
      ),
    [handleRefreshSession],
  )

  // Two different things can lapse, so this keeps both alive.
  //
  // 1. The legacy session row the browser presents. Its expiry is fixed at the
  //    moment it is issued, so the only way to extend it is POST
  //    /v1/auth/refresh, which rotates the TokenID and issues a fresh cookie.
  //    This is the common path and costs no Auth0 round-trip.
  // 2. The Auth0 token family. Since the only endpoint that accepts an
  //    access_token is the exchange endpoint, no service 401 can drive this for
  //    us — it has to happen on a timer.
  //
  // Order matters: the refresh needs a live session row, so it cannot recover a
  // row that has already died; the re-exchange can, because it authenticates on
  // the Auth0 bearer instead. Refresh first, re-exchange only as the fallback.
  useEffect(() => {
    if (!useIdpAuthentication) {
      return undefined
    }

    const slideSession = async (): Promise<void> => {
      const response = await api.auth.refresh()

      if (response.result === 'success') {
        return
      }

      // `silent` is essential, not incidental: a non-silent re-exchange passes
      // through `pending`, which unmounts the protected subtree and destroys an
      // open photo uploader — the failure this whole change exists to stop.
      await handleRefreshSession({silent: true})
    }

    const interval = setInterval(() => {
      slideSession()
    }, SESSION_REFRESH_INTERVAL_MS)

    return () => {
      clearInterval(interval)
    }
  }, [useIdpAuthentication, handleRefreshSession])

  return null
}
